import { Auth } from 'aws-amplify';
import { emptySplitApi } from 'redux/helpers/slice.helpers';
import { EAuthUrls } from 'data/urls/auth.url';
import {
  resetPasswordCasesConverter,
  resetPasswordConverter,
  resetPasswordFormConverter,
  setNewPasswordConverter,
} from 'data/convertors/auth.convertors';
import {
  TResetPasswordCasesUI,
  TResetPasswordDb,
  TResetPasswordFormUI,
  TResetPasswordUI,
  TSetPasswordFormUI,
} from 'data/types/Auth.types';
import { TAmplifyError, TAmplifyUser } from 'data/types/Amplify.types';
import { claimSavedFilters } from 'helpers/storage';
import { filtersSlice } from 'redux/slices/filters.slice';

const forgotPasswordUrl = `${EAuthUrls.AUTH}/${EAuthUrls.FORGOT_PASSWORD}`;
const temporaryPasswordUrl = `${EAuthUrls.AUTH}/${EAuthUrls.TEMPORARY_PASSWORD}`;
const isAdminUrl = `${EAuthUrls.AUTH}/${EAuthUrls.IS_ADMIN}`;

export const authPasswordApi = emptySplitApi(
  'authPassword',
  'property_manager',
).injectEndpoints({
  endpoints: build => ({
    resetPasswordCases: build.mutation<TResetPasswordUI, TResetPasswordCasesUI>(
      {
        query(data) {
          return {
            url: forgotPasswordUrl,
            method: 'POST',
            body: resetPasswordCasesConverter.toDb(data),
          };
        },
        transformResponse: (data: TResetPasswordDb) =>
          resetPasswordConverter.fromDb(data),
      },
    ),
    resetPasswordAmplify: build.mutation<null, TResetPasswordFormUI>({
      // @ts-expect-error -- the error branch returns Amplify's error shape, which RTK's
      // FetchBaseQueryError type does not describe.
      async queryFn(data) {
        const { username, password, code } =
          resetPasswordFormConverter.toDb(data);
        try {
          await Auth.forgotPasswordSubmit(username, code, password);
          return { data: null };
        } catch (e) {
          const error = e as TAmplifyError;
          return {
            error: {
              data: {
                name: error.name,
                code: error.code,
                message: error.message,
              },
              status: error.code,
            },
          };
        }
      },
    }),
    setNewPassword: build.mutation<TAmplifyUser, TSetPasswordFormUI>({
      // @ts-expect-error -- the error branch returns Amplify's error shape, which RTK's
      // FetchBaseQueryError type does not describe.
      async queryFn(arg, api, _extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: temporaryPasswordUrl,
            method: 'PATCH',
            body: setNewPasswordConverter.toDb(arg),
          });
          if (result.error) {
            return { error: result.error };
          }
          const amplifyUser = await Auth.signIn(arg.email, arg.password);
          localStorage.setItem('user', JSON.stringify(amplifyUser));
          if (claimSavedFilters(amplifyUser.username)) {
            api.dispatch(filtersSlice.actions.reloadSavedFilters());
          }
          return { data: amplifyUser.attributes };
        } catch (e) {
          const error = e as TAmplifyError;
          return {
            error: {
              data: {
                name: error.name,
                code: error.code,
                message: error.message,
              },
              status: error.code,
            },
          };
        }
      },
    }),
    getIsAdmin: build.query<boolean, { username: string }>({
      query({ username }) {
        return {
          url: isAdminUrl,
          method: 'GET',
          params: { username },
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useResetPasswordCasesMutation,
  useResetPasswordAmplifyMutation,
  useSetNewPasswordMutation,
  useGetIsAdminQuery,
} = authPasswordApi;
